import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';

import { FineAdjustmentService } from '@/app/core/services/fine-adjustment/fine-adjustment.service';
import {
  GetFineAdjustmentAction,
  ApplyFineAdjustmentAction,
  ClearFineAdjustmentValuesAction,
  ResetClearingFlagAction,
} from './fine-adjustment.actions';
import { catchError, tap, throwError } from 'rxjs';
import { IFineAdjustment } from '@/app/core/interfaces/fine-adjustment.interface';
import { MatDialog } from '@angular/material/dialog';
import { EnovaAlertsComponent } from '@/app/core/utils/enova-alerts/enova-alerts.component';

export interface FineAdjustmentStateModel {
  fineAdjustmentData: IFineAdjustment[];
  filters: any;
  isClearing: boolean;
}

export const initialState: FineAdjustmentStateModel = {
  fineAdjustmentData: [],
  filters: null,
  isClearing: false,
};

@State<FineAdjustmentStateModel>({
  name: 'fineAdjustment',
  defaults: initialState,
})
@Injectable()
export class FineAdjustmentState {
  @Selector() static getFineAdjustmentData(
    state: FineAdjustmentStateModel
  ): IFineAdjustment[] {
    return state?.fineAdjustmentData || null;
  }

  @Selector() static getIsClearing(state: FineAdjustmentStateModel): boolean {
    return state?.isClearing || false;
  }

  constructor(
    private fineAdjustmentService: FineAdjustmentService,
    private dialog: MatDialog
  ) {}

  @Action(GetFineAdjustmentAction)
  GetFineAdjustmentAction(
    ctx: StateContext<FineAdjustmentStateModel>,
    { payload }: GetFineAdjustmentAction
  ) {
    ctx.patchState({ filters: payload.filters });
    return this.fineAdjustmentService.getFineAdjustment(payload).pipe(
      tap(response => {
        ctx.patchState({ 
          fineAdjustmentData: response,
          isClearing: false // Resetear el flag después de cargar los datos
        });
      }),
      catchError(error => {
        // También resetear el flag en caso de error
        ctx.patchState({ isClearing: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text:
              error?.error?.error ||
              error?.details ||
              error?.message ||
              'Ha ocurrido un error al obtener los datos',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(ApplyFineAdjustmentAction)
  ApplyFineAdjustmentAction(
    ctx: StateContext<FineAdjustmentStateModel>,
    { payload }: ApplyFineAdjustmentAction
  ) {
    return this.fineAdjustmentService.applyFineAdjustment(payload).pipe(
      tap(() => {
        // Mostrar mensaje de éxito
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'Ajuste fino aplicado correctamente',
            hiddeCancelBtn: true,
          },
        });

        // Refrescar los datos después de aplicar el ajuste
        const currentState = ctx.getState();
        if (currentState.fineAdjustmentData.length > 0) {
          ctx.dispatch(
            new GetFineAdjustmentAction({
              startDate: currentState.fineAdjustmentData[0]?.fecha,
              endDate:
                currentState.fineAdjustmentData[
                  currentState.fineAdjustmentData.length - 1
                ]?.fecha,
              filters: currentState.filters,
            })
          );
        }
      }),
      catchError(error => {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text:
              error?.error?.error ||
              error?.details ||
              error?.message ||
              'Ha ocurrido un error al aplicar el ajuste fino',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(ClearFineAdjustmentValuesAction)
  ClearFineAdjustmentValuesAction(ctx: StateContext<FineAdjustmentStateModel>) {
    ctx.patchState({ isClearing: true });
  }

  @Action(ResetClearingFlagAction)
  ResetClearingFlagAction(ctx: StateContext<FineAdjustmentStateModel>) {
    ctx.patchState({ isClearing: false });
  }
}
